import React from 'react';
import { StaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';
import styled from 'styled-components';
import {
  breakpointSizes,
  breakpointQuery,
  standardColours,
  brandColours,
  fluidFontSize,
} from '../utilities';
import Container from './Container';
import Animation from './Animation';

const StyledBanner = styled.section`
  position: relative;
  text-align: center;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${brandColours.secondary};
    opacity: 0.75;
  }
`;

const StyledBannerImageWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  ${breakpointQuery.medium`
    position: relative;
  `}
`;

const StyledBannerImage = styled(Img)`
  height: 100%;
  max-height: 480px;
`;

const StyledBannerInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 40px 0;
  min-height: 280px;

  ${breakpointQuery.tiny`
    min-height: 300px;
  `}

  ${breakpointQuery.small`
    padding-top: 50px;
    padding-bottom: 50px;
    min-height: 320px;
  `}

  ${breakpointQuery.medium`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    min-height: auto;
  `}

  ${breakpointQuery.large`
    padding-top: 60px;
    padding-bottom: 60px;
  `}
`;

const StyledBannerHeading = styled.h1`
  color: ${standardColours.white};
  ${fluidFontSize('26px', '46px', breakpointSizes.tiny, breakpointSizes.medium)}
  line-height: 1.2;

  ${breakpointQuery.medium`
    ${fluidFontSize(
      '36px',
      '56px',
      breakpointSizes.medium,
      breakpointSizes.xxlarge
    )}
  `}
`;

const StyledBannerText = styled.p`
  color: ${standardColours.white};
  ${fluidFontSize(
    '18px',
    '28px',
    breakpointSizes.tiny,
    breakpointSizes.xxlarge
  )}
`;

const Banner = ({
  data: {
    datoCmsBannerImage: {
      defaultBannerImage: { fluid: defaultImageFluid, alt: defaultImageAlt },
    },
  },
  heading,
  text,
  image,
}) => {
  const fluid = image ? image.fluid : defaultImageFluid;
  const alt = image ? image.alt : defaultImageAlt;
  return (
    <StyledBanner>
      <StyledBannerImageWrap>
        <StyledBannerImage fluid={fluid} alt={alt} />
      </StyledBannerImageWrap>
      <StyledBannerInner>
        <Container>
          <Animation>
            <StyledBannerHeading>{heading}</StyledBannerHeading>
            {text && <StyledBannerText>{text}</StyledBannerText>}
          </Animation>
        </Container>
      </StyledBannerInner>
    </StyledBanner>
  );
};

export default props => (
  <StaticQuery
    query={graphql`
      query BannerQuery {
        datoCmsBannerImage {
          defaultBannerImage {
            ...BannerImageFragment
          }
        }
      }
    `}
    render={data => <Banner data={data} {...props} />}
  />
);

export const BannerImageFragment = graphql`
  fragment BannerImageFragment on DatoCmsFileField {
    fluid(
      maxHeight: 500
      maxWidth: 1600
      imgixParams: { auto: "compress", fit: "crop", h: "500", w: "1600" }
    ) {
      ...GatsbyDatoCmsFluid
    }
    alt
  }
`;
